import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useI18n } from '@/lib/i18n'
import { LANGS, langPrefixPath, stripLangPrefix } from '@/lib/lang'

interface SeoProps {
  title: string
  description: string
  keywords?: string[]
}

function upsertLink(rel: string, href: string, id: string): void {
  const existing = document.querySelector<HTMLLinkElement>(`link[data-seo="${id}"]`)
  if (existing) {
    existing.href = href
  } else {
    const link = document.createElement('link')
    link.rel = rel
    link.href = href
    link.dataset.seo = id
    document.head.appendChild(link)
  }
}

/**
 * Lightweight SEO helper: sets document title + meta description/keywords and
 * manages canonical + hreflang alternate links for the current language.
 */
export function Seo({ title, description, keywords }: SeoProps): null {
  const { lang } = useI18n()
  const location = useLocation()

  useEffect(() => {
    document.title = title
    document.documentElement.lang = lang

    const descriptionEl = document.querySelector<HTMLMetaElement>('meta[name="description"]')
    descriptionEl?.setAttribute('content', description)

    const keywordsEl = document.querySelector<HTMLMetaElement>('meta[name="keywords"]')
    keywordsEl?.setAttribute('content', keywords?.join(', ') ?? '')

    const origin = window.location.origin
    const bare = stripLangPrefix(location.pathname)
    upsertLink('canonical', `${origin}${langPrefixPath(bare, lang)}`, 'canonical')
    for (const candidate of LANGS) {
      upsertLink(
        'alternate',
        `${origin}${langPrefixPath(bare, candidate)}`,
        `alternate-${candidate}`,
      )
    }
  }, [title, description, keywords, lang, location.pathname])

  return null
}